import React from 'react';
import HarbourIcon from './HarbourIcon';
import HarbourIconButton from './HarbourIconButton';
import HarbourTextLink from './HarbourTextLink';
import { formatUkPhone, telHref } from '../../utils/contactFormatting';
import { formatWhen, teacherRowState } from '../../utils/schoolTeachers';
import './harbour.css';

/*
 * Harbour/Teachers box (TDI-53 section 30, restructured per design T-15).
 * The record lists people, not work: each teacher is a contact row with one
 * Edit control, and "Add a teacher" is its own row at the end. Who holds
 * which forms, and how far they have got, is shown under the pathway's
 * School forms block instead — the record used to carry both and said the
 * same thing twice.
 *
 * Each row is a teacher from the school's list (TDI-616): name, role under
 * the name, then phone and email links.
 *
 * Once a teacher holds forms, the row also says where they are and offers
 * its one action (TDI-618): "Email sent {date}", "Signed in" or "{x} of {y}
 * forms done", with "Send the email again" until they have set a password,
 * then "Send a reminder" while forms are unfinished ("Reminder sent {time}"
 * for 24 hours after one).
 */
export default function HarbourTeachersBox({
  teachers = [], formerTeachers = [], schoolName = null,
  onAdd, onEdit, formProgress = {}, onResend, resendingId = null, now,
}) {
  const addRow = (
    <div className="harbour-parents-box__row">
      <HarbourTextLink leadingIcon="plus" onClick={onAdd}>
        Add a teacher
      </HarbourTextLink>
    </div>
  );

  // A teacher who left keeps their row on each pupil, greyed (TDI-774,
  // R-045): their finished forms still say who completed them, and the row
  // says plainly why they can no longer be chased or changed. Bring back
  // returns them to the school's list only; reassigning them through "Add a
  // teacher" makes them a live row again.
  const formerRows = (formerTeachers || []).map((t, i) => {
    const name = [t.firstName, t.lastName].filter(Boolean).join(' ') || t.email;
    return (
      <div
        key={`former-${t.participantId || t.id || t.email || i}`}
        className="harbour-parents-box__row harbour-teachers-box__row--former"
      >
        <div className="flex flex-col min-w-0 gap-0.5">
          <span className="harbour-parents-box__label">Teacher</span>
          <span className="harbour-parents-box__name">
            {t.jobRole ? `${name}, ${t.jobRole}` : name}
          </span>
          <span className="harbour-teachers-box__state">
            {schoolName ? `No longer at ${schoolName}` : 'No longer at the school'}
          </span>
        </div>
      </div>
    );
  });

  if (!teachers.length) {
    return (
      <div className="harbour-parents-box" data-testid="harbour-teachers-box">
        {formerRows}
        <div className="harbour-parents-box__row">
          <div className="flex flex-col min-w-0">
            <span className="harbour-parents-box__label">Teacher</span>
            <span className="harbour-teachers-box__empty">Not added yet</span>
          </div>
          <div className="ml-auto flex-none">
            <HarbourTextLink leadingIcon="plus" onClick={onAdd}>
              Add a teacher
            </HarbourTextLink>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="harbour-parents-box" data-testid="harbour-teachers-box">
      {formerRows}
      {teachers.map((t, i) => {
        const name = [t.firstName, t.lastName].filter(Boolean).join(' ');
        const rowState = teacherRowState(t, formProgress, now || new Date());
        const rowId = t.participantId || t.id;
        const first = t.firstName || name || 'them';
        return (
          <div key={t.participantId || t.id || t.email || i} className="harbour-parents-box__row">
            <div className="flex flex-col min-w-0 gap-0.5">
              <span className="harbour-parents-box__label">Teacher</span>
              <span className="harbour-parents-box__name">{name || t.email}</span>
              {/* The role from the school's list (TDI-616). Teachers listed
                  before school lists existed have none until a SENCO adds it,
                  and those without an email cannot be given forms. */}
              <span className="harbour-teachers-box__role">{t.jobRole || 'Role not added yet'}</span>
              {!t.email && (
                <span className="harbour-teachers-box__role">Add an email to give them forms</span>
              )}
              {rowState.label && (
                <span className="harbour-teachers-box__state">{rowState.label}</span>
              )}
              {onResend && rowState.action === 'resend' && (
                <span>
                  <HarbourTextLink
                    disabled={resendingId === rowId}
                    aria-label={`Send ${first} the email again`}
                    onClick={() => onResend(t)}
                  >
                    {resendingId === rowId ? 'Sending…' : 'Send the email again'}
                  </HarbourTextLink>
                </span>
              )}
              {onResend && rowState.action === 'remind' && (
                <span>
                  <HarbourTextLink
                    disabled={resendingId === rowId}
                    aria-label={`Send ${first} a reminder`}
                    onClick={() => onResend(t)}
                  >
                    {resendingId === rowId ? 'Sending…' : 'Send a reminder'}
                  </HarbourTextLink>
                </span>
              )}
              {rowState.action === 'reminded' && (
                <span className="harbour-teachers-box__state">
                  {`Reminder sent ${formatWhen(rowState.reminderSentAt, now || new Date())}`}
                </span>
              )}
            </div>
            <div className="ml-auto flex items-center gap-6 flex-wrap justify-end">
              {t.phone && (
                <a className="harbour-parents-box__contact" href={telHref(t.phone)}>
                  <HarbourIcon name="phone" size={20} />
                  {formatUkPhone(t.phone)}
                </a>
              )}
              {t.email && (
                <a className="harbour-parents-box__contact" href={`mailto:${t.email}`}>
                  <HarbourIcon name="email" size={20} />
                  {t.email}
                </a>
              )}
              {onEdit && (
                <HarbourIconButton
                  kind="ghost"
                  icon="edit"
                  label={`Change ${name || 'this teacher'}`}
                  onClick={() => onEdit(t)}
                />
              )}
            </div>
          </div>
        );
      })}
      {addRow}
    </div>
  );
}
